import { forwardRef, ReactNode, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { ITestable } from '@belong/types';
import { Copy } from '../../styles/Typography/Copy/Copy';
import { InlineSpinner, InlineSpinnerContainer, InlineSpinnerText } from './LoadingSpinner.styles';

export interface IInlineSpinnerProps extends ITestable {
  className?: string;
  /**
   * The text content to display next to the spinner
   */
  children?: ReactNode;
  /** Optional title shown next to the spinner when no children are provided */
  title?: string;
  /** Optional description shown under the title when no children are provided */
  description?: string;
  /** If true, focuses the loading text on mount for programmatic announcements */
  autoFocus?: boolean;
  /** If true, hides the spinner content from assistive technology */
  ariaHidden?: boolean;
}

/**
 * The `InlineSpinner` component is used for contextual loading feedback without blocking the entire page.
 * It displays a 32px spinner inline with text, suitable for actions that require short processing time.
 * Use this component for any action that cannot be performed instantly and requires a short processing time,
 * such as retrieving or refreshing small data amounts (e.g., an address).
 */
const useClientLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export const InlineSpinnerComponent = forwardRef<HTMLSpanElement, IInlineSpinnerProps>(
  (
    {
      className,
      children,
      title = 'Loading',
      description = '',
      autoFocus = false,
      ariaHidden = false,
      'data-testid': dataTestid = 'inlineSpinner'
    },
    ref
  ) => {
    const localRef = useRef<HTMLSpanElement>(null);

    useImperativeHandle(ref, () => localRef.current as HTMLSpanElement, []);

    useClientLayoutEffect(() => {
      if (!autoFocus || ariaHidden) {
        return;
      }

      const frame = window.requestAnimationFrame(() => {
        localRef.current?.focus();
      });

      return () => {
        window.cancelAnimationFrame(frame);
      };
    }, [ariaHidden, autoFocus]);

    const fallback = (
      <>
        {title && <Copy isBold>{title}</Copy>}
        {description && <Copy>{description}</Copy>}
      </>
    );

    return (
      <InlineSpinnerContainer className={className} data-testid={dataTestid} aria-hidden={ariaHidden || undefined}>
        <InlineSpinner aria-hidden="true" role="presentation" />
        <InlineSpinnerText
          ref={localRef}
          tabIndex={ariaHidden ? undefined : -1}
          role={ariaHidden ? undefined : 'status'}
          aria-live={ariaHidden ? undefined : 'polite'}
          aria-atomic={ariaHidden ? undefined : 'true'}
        >
          {children ?? fallback}
        </InlineSpinnerText>
      </InlineSpinnerContainer>
    );
  }
);

InlineSpinnerComponent.displayName = 'InlineSpinner';
